import { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, Search, Package, Save, X, Edit3, DollarSign } from "lucide-react";
import api from "../lib/api";
import { fmtBRL, PAY_LABELS, PAY_COLORS, HEADER_BG, monthLabel, COLOR_MAP } from "../lib/constants";
import { useAuth } from "../lib/auth";
import ConfirmDialog from "../components/ConfirmDialog";
import PriceLookupModal from "../components/PriceLookupModal";

// Devolvido não é aplicável para vendas avulsas
const PS_PAY_ENTRIES = Object.entries(PAY_LABELS).filter(([k]) => k !== "devolvido");
const COLORS = Object.keys(COLOR_MAP);
const EMPTY = { clientStore: "", piece: "", value: 0, costValue: 0, payMethod: "", observation: "" };

const parseCurrency = (v) => (typeof v === "number" ? v : parseFloat(String(v || "").replace(/\./g, "").replace(",", ".")) || 0);

function PayBadge({ payMethod, onClick }) {
  if (!payMethod)
    return (
      <button onClick={onClick} className="text-[10px] italic text-slate-400 hover:text-slate-600">— selecionar —</button>
    );
  const pc = PAY_COLORS[payMethod];
  return (
    <button onClick={onClick} className="text-[10px] font-bold px-2 py-0.5 rounded-full border whitespace-nowrap" style={{ background: pc.bg, color: pc.color, borderColor: pc.border }}>
      {PAY_LABELS[payMethod]}
    </button>
  );
}

export default function VendaPecasView({ pieceSales, searchQuery, clients = [], onRefresh }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canSeeCost = isAdmin;
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [payFilter, setPayFilter] = useState("all");
  const [monthFilter, setMonthFilter] = useState("all");
  const [clientQuery, setClientQuery] = useState("");
  const [showClientList, setShowClientList] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const [parts, setParts] = useState([]);
  const [showPartsModal, setShowPartsModal] = useState(false);
  const [pieceQuery, setPieceQuery] = useState("");
  const [showPieceList, setShowPieceList] = useState(false);
  const [showPriceLookup, setShowPriceLookup] = useState(false);

  // inline editing
  const [edit, setEdit] = useState({ id: null, field: null }); // field: piece|value|costValue|payMethod|observation

  useEffect(() => { api.get("/parts").then((r) => setParts(r.data)); }, []);
  const refreshParts = async () => setParts((await api.get("/parts")).data);

  const clientMatches = useMemo(() => {
    const q = clientQuery.toLowerCase().trim();
    if (!q) return [];
    return clients.filter((c) => c.name.toLowerCase().includes(q) || c.phone.includes(clientQuery)).slice(0, 6);
  }, [clientQuery, clients]);

  const partMatches = useMemo(() => {
    const q = pieceQuery.toLowerCase().trim();
    if (!q) return [];
    return parts.filter((p) => p.name.toLowerCase().includes(q) || (p.color || "").toLowerCase().includes(q)).slice(0, 8);
  }, [pieceQuery, parts]);

  const availableMonths = useMemo(() => {
    const seen = new Set();
    pieceSales.forEach((s) => { const p = (s.date || "").split("/"); if (p.length >= 2) seen.add(p.slice(1).join("/")); });
    return Array.from(seen).sort();
  }, [pieceSales]);

  const filtered = useMemo(() => {
    const q = (searchQuery || "").toLowerCase();
    return pieceSales.filter((s) => {
      if (payFilter !== "all") {
        if (payFilter === "none" && s.payMethod) return false;
        if (payFilter !== "none" && s.payMethod !== payFilter) return false;
      }
      if (monthFilter !== "all") {
        const p = (s.date || "").split("/"); const key = p.slice(1).join("/");
        if (key !== monthFilter) return false;
      }
      if (!q) return true;
      return (s.clientStore || "").toLowerCase().includes(q) || (s.piece || "").toLowerCase().includes(q);
    });
  }, [pieceSales, searchQuery, payFilter, monthFilter]);

  const totals = useMemo(() => {
    const receita = filtered.reduce((s, x) => s + (Number(x.value) || 0), 0);
    const custo = filtered.reduce((s, x) => s + (Number(x.costValue) || 0), 0);
    return { receita, custo, lucro: receita - custo };
  }, [filtered]);

  const add = async () => {
    if (!form.clientStore.trim() || !form.piece.trim() || saving) return;
    setSaving(true);
    try {
      await api.post("/piece-sales", {
        ...form,
        payMethod: form.payMethod || null,
        value: parseCurrency(form.value),
        costValue: parseCurrency(form.costValue),
      });
      setForm(EMPTY); setPieceQuery(""); onRefresh();
    } finally { setSaving(false); }
  };

  const patchSale = async (id, patch) => {
    const r = await api.patch(`/piece-sales/${id}`, patch);
    onRefresh();
    return r.data;
  };

  const del = async (id) => { await api.delete(`/piece-sales/${id}`); onRefresh(); };

  const pickPart = (p) => {
    setForm({ ...form, piece: p.name + (p.color ? ` ${p.color}` : ""), value: p.value, costValue: p.cost });
    setPieceQuery(""); setShowPieceList(false);
  };

  return (
    <div className="p-6 space-y-4" data-testid="vendapecas-view">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-heading text-2xl font-bold text-slate-900">Venda de peças</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            {filtered.length} venda(s) · Receita <b>R$ {fmtBRL(totals.receita)}</b>
            {canSeeCost && (<> · Custo <b className="text-rose-700">R$ {fmtBRL(totals.custo)}</b> · Lucro <b className="text-emerald-700">R$ {fmtBRL(totals.lucro)}</b></>)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <button
              onClick={() => setShowPriceLookup(true)}
              data-testid="open-price-lookup"
              className="h-10 px-4 rounded-lg text-sm font-semibold bg-white border border-slate-300 text-slate-700 hover:border-blue-400 hover:text-blue-700 inline-flex items-center gap-2 transition-all"
              title="Consultar tabela de preços"
            >
              <DollarSign className="w-4 h-4" /> Consultar preços
            </button>
          )}
          <button
            onClick={() => setShowPartsModal(true)}
            data-testid="open-parts-catalog"
            className="h-10 px-4 rounded-lg text-sm font-semibold bg-white border border-slate-300 text-slate-700 hover:border-blue-400 hover:text-blue-700 inline-flex items-center gap-2 transition-all"
          >
            <Package className="w-4 h-4" /> Cadastro de peças
          </button>
        </div>
      </div>

      {/* Quick add form */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
        <div className={`grid grid-cols-1 gap-2 items-end ${canSeeCost ? "md:grid-cols-7" : "md:grid-cols-6"}`}>
          <div className="md:col-span-2 relative">
            <label className="text-[10px] uppercase font-semibold text-slate-500">Cliente / Loja</label>
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-300 pointer-events-none" />
              <input
                value={form.clientStore}
                onChange={(e) => { setForm({ ...form, clientStore: e.target.value }); setClientQuery(e.target.value); setShowClientList(true); }}
                onFocus={() => setShowClientList(true)}
                onBlur={() => setTimeout(() => setShowClientList(false), 200)}
                placeholder="Cliente cadastrado ou nome livre"
                className="w-full h-9 rounded-md border border-slate-300 pl-8 pr-2.5 text-sm focus:border-blue-500 focus:outline-none"
                data-testid="ps-clientstore"
              />
              {showClientList && clientMatches.length > 0 && (
                <ul className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden max-h-56 overflow-y-auto">
                  {clientMatches.map((c) => (
                    <li key={c.id}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => { setForm({ ...form, clientStore: c.name }); setClientQuery(""); setShowClientList(false); }}
                      className="px-3 py-2 text-sm hover:bg-blue-50 cursor-pointer border-b border-slate-100 last:border-0"
                    >
                      <div className="font-medium text-slate-800">{c.name}</div>
                      <div className="text-[11px] text-slate-400">{c.phone}</div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          <div className="md:col-span-2 relative">
            <label className="text-[10px] uppercase font-semibold text-slate-500">Peça</label>
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-300 pointer-events-none" />
              <input
                value={form.piece}
                onChange={(e) => { setForm({ ...form, piece: e.target.value }); setPieceQuery(e.target.value); setShowPieceList(true); }}
                onFocus={() => setShowPieceList(true)}
                onBlur={() => setTimeout(() => setShowPieceList(false), 200)}
                placeholder="Buscar do cadastro ou digitar"
                className="w-full h-9 rounded-md border border-slate-300 pl-8 pr-2.5 text-sm focus:border-blue-500 focus:outline-none"
                data-testid="ps-piece"
              />
              {showPieceList && partMatches.length > 0 && (
                <ul className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden max-h-56 overflow-y-auto">
                  {partMatches.map((p) => (
                    <li key={p.id}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => pickPart(p)}
                      className="px-3 py-2 text-sm hover:bg-blue-50 cursor-pointer border-b border-slate-100 last:border-0"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div>
                          <div className="font-medium text-slate-800">{p.name}</div>
                          <div className="text-[11px] text-slate-400">
                            {p.color || "—"}
                            {canSeeCost && (<> · custo R$ {fmtBRL(p.cost)}</>)}
                          </div>
                        </div>
                        <div className="text-sm font-bold text-blue-700 tabular-nums">R$ {fmtBRL(p.value)}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase font-semibold text-slate-500">Valor</label>
            <input type="number" step="0.01" value={form.value}
              onChange={(e) => setForm({ ...form, value: parseFloat(e.target.value) || 0 })}
              className="w-full h-9 rounded-md border border-slate-300 px-2.5 text-sm text-right focus:border-blue-500 focus:outline-none"
              data-testid="ps-value"
            />
          </div>
          {canSeeCost && (
            <div>
              <label className="text-[10px] uppercase font-semibold text-slate-500">Custo</label>
              <input type="number" step="0.01" value={form.costValue}
                onChange={(e) => setForm({ ...form, costValue: parseFloat(e.target.value) || 0 })}
                className="w-full h-9 rounded-md border border-slate-300 px-2.5 text-sm text-right focus:border-blue-500 focus:outline-none"
                data-testid="ps-cost"
              />
            </div>
          )}
          <div>
            <label className="text-[10px] uppercase font-semibold text-slate-500">Pagamento</label>
            <select value={form.payMethod}
              onChange={(e) => setForm({ ...form, payMethod: e.target.value })}
              className="w-full h-9 rounded-md border border-slate-300 px-2 text-sm bg-white focus:border-blue-500 focus:outline-none"
              data-testid="ps-pay"
            >
              <option value="">—</option>
              {PS_PAY_ENTRIES.map(([k, v]) => (<option key={k} value={k}>{v}</option>))}
            </select>
          </div>
        </div>
        <div className="mt-3 flex justify-end">
          <button onClick={add} disabled={saving} data-testid="ps-add"
            className="h-9 px-4 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold inline-flex items-center gap-1.5 transition-all hover:-translate-y-[1px] disabled:opacity-50"
          >
            <Plus className="w-4 h-4" /> Adicionar venda
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        <button onClick={() => setMonthFilter("all")}
          className={`px-3 py-1.5 rounded-md text-xs font-semibold ${monthFilter === "all" ? "bg-slate-900 text-white" : "bg-white text-slate-600 border border-slate-200 hover:border-slate-300"}`}
        >Todos meses</button>
        {availableMonths.map((m) => (
          <button key={m} onClick={() => setMonthFilter(m)}
            className={`px-3 py-1.5 rounded-md text-xs font-semibold ${monthFilter === m ? "bg-slate-900 text-white" : "bg-white text-slate-600 border border-slate-200 hover:border-slate-300"}`}
          >{m.length > 5 ? monthLabel(m) : m}</button>
        ))}
        <div className="w-px bg-slate-200 mx-1" />
        <button onClick={() => setPayFilter("all")}
          className={`px-3 py-1.5 rounded-md text-xs font-semibold ${payFilter === "all" ? "bg-blue-600 text-white shadow-sm" : "bg-white text-slate-600 border border-slate-200 hover:border-blue-300"}`}
        >Todos pagtos</button>
        {PS_PAY_ENTRIES.map(([k, v]) => (
          <button key={k} onClick={() => setPayFilter(k)}
            className={`px-3 py-1.5 rounded-md text-xs font-semibold ${payFilter === k ? "bg-blue-600 text-white shadow-sm" : "bg-white text-slate-600 border border-slate-200 hover:border-blue-300"}`}
          >{v}</button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: HEADER_BG }}>
                {["DATA", "HORA", "CLIENTE/LOJA", "PEÇA", "VALOR", ...(canSeeCost ? ["CUSTO"] : []), "PAGAMENTO", "OBS.", ""].map((h) => (
                  <th key={h} className="text-left text-[10px] font-bold uppercase tracking-wider text-white px-3 py-2.5 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={canSeeCost ? 9 : 8} className="text-center py-10 text-slate-400 text-sm">Nenhuma venda encontrada.</td></tr>
              )}
              {filtered.map((s) => (
                <tr key={s.id} className="border-b border-slate-100 last:border-0 hover:bg-blue-50/30">
                  <td className="px-3 py-2 text-xs text-slate-700 whitespace-nowrap">{s.date}</td>
                  <td className="px-3 py-2 text-xs text-slate-500 tabular-nums whitespace-nowrap">{s.time}</td>
                  <td className="px-3 py-2 text-xs font-semibold text-slate-800">{s.clientStore}</td>
                  <td className="px-3 py-2 text-xs text-slate-700">
                    {edit.id === s.id && edit.field === "piece" ? (
                      <input autoFocus defaultValue={s.piece} onBlur={async (e) => { const v = e.target.value.trim(); if (v && v !== s.piece) await patchSale(s.id, { piece: v }); setEdit({ id: null, field: null }); }} onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()} className="w-full text-xs border border-blue-300 rounded px-1.5 py-0.5 bg-blue-50 focus:outline-none" />
                    ) : (
                      <button onClick={() => setEdit({ id: s.id, field: "piece" })} className="hover:text-blue-700 hover:underline text-left">{s.piece}</button>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs font-semibold text-slate-800 tabular-nums whitespace-nowrap">
                    {edit.id === s.id && edit.field === "value" ? (
                      <input autoFocus defaultValue={fmtBRL(s.value)} onBlur={async (e) => { const v = parseCurrency(e.target.value); if (v !== (s.value || 0)) await patchSale(s.id, { value: v }); setEdit({ id: null, field: null }); }} onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()} className="w-20 text-xs text-right border border-blue-300 rounded px-1.5 py-0.5 bg-blue-50 focus:outline-none" />
                    ) : (
                      <button onClick={() => setEdit({ id: s.id, field: "value" })} className="hover:text-blue-700">R$ {fmtBRL(s.value)}</button>
                    )}
                  </td>
                  {canSeeCost && (
                    <td className="px-3 py-2 text-xs tabular-nums whitespace-nowrap">
                      {edit.id === s.id && edit.field === "costValue" ? (
                        <input autoFocus defaultValue={s.costValue ? fmtBRL(s.costValue) : ""} placeholder="0,00" onBlur={async (e) => { const v = parseCurrency(e.target.value); if (v !== (s.costValue || 0)) await patchSale(s.id, { costValue: v }); setEdit({ id: null, field: null }); }} onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()} className="w-20 text-xs text-right border border-rose-300 rounded px-1.5 py-0.5 bg-rose-50 focus:outline-none" />
                      ) : (
                        <button onClick={() => setEdit({ id: s.id, field: "costValue" })} className={s.costValue ? "text-rose-700 hover:text-rose-900" : "text-slate-300 hover:text-rose-600"}>
                          {s.costValue ? `R$ ${fmtBRL(s.costValue)}` : "— editar —"}
                        </button>
                      )}
                    </td>
                  )}
                  <td className="px-3 py-2">
                    {edit.id === s.id && edit.field === "payMethod" ? (
                      <select autoFocus value={s.payMethod || ""} onChange={async (e) => { await patchSale(s.id, { payMethod: e.target.value || null }); setEdit({ id: null, field: null }); }} onBlur={() => setEdit({ id: null, field: null })} className="text-[10px] rounded border border-slate-300 px-1 py-0.5 bg-white focus:outline-none">
                        <option value="">—</option>
                        {PS_PAY_ENTRIES.map(([k, v]) => (<option key={k} value={k}>{v}</option>))}
                      </select>
                    ) : (
                      <PayBadge payMethod={s.payMethod} onClick={() => setEdit({ id: s.id, field: "payMethod" })} />
                    )}
                  </td>
                  <td className="px-3 py-2 max-w-[180px]">
                    {edit.id === s.id && edit.field === "observation" ? (
                      <input autoFocus defaultValue={s.observation || ""} onBlur={async (e) => { await patchSale(s.id, { observation: e.target.value }); setEdit({ id: null, field: null }); }} onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()} className="w-full text-[11px] border border-blue-300 rounded px-1.5 py-0.5 bg-blue-50 focus:outline-none" />
                    ) : (
                      <button onClick={() => setEdit({ id: s.id, field: "observation" })} className="text-[11px] text-slate-600 hover:text-blue-700 truncate max-w-full text-left" title={s.observation || "Adicionar obs."}>
                        {s.observation || <span className="italic text-slate-300">— adicionar —</span>}
                      </button>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => setConfirmDelete(s)} className="p-1.5 text-slate-300 hover:text-rose-600 hover:bg-rose-50 rounded" title="Excluir" data-testid={`ps-delete-${s.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showPartsModal && (
        <PartsCatalogModal parts={parts} canSeeCost={canSeeCost} onClose={() => setShowPartsModal(false)} onChange={refreshParts} />
      )}

      {showPriceLookup && (
        <PriceLookupModal
          initialQuery={form.piece || ""}
          onClose={() => setShowPriceLookup(false)}
          onPick={(item) => {
            const label = [item.device, item.service].filter(Boolean).join(" — ");
            setForm({
              ...form,
              piece: label,
              value: Number(item.price) || 0,
              costValue: Number(item.cost) || 0,
            });
            setShowPriceLookup(false);
          }}
        />
      )}

      <ConfirmDialog
        open={!!confirmDelete}
        title="Excluir venda"
        message={confirmDelete ? `Excluir a venda "${confirmDelete.piece}"?` : ""}
        confirmLabel="Excluir"
        onCancel={() => setConfirmDelete(null)}
        onConfirm={async () => { const id = confirmDelete.id; setConfirmDelete(null); await del(id); }}
      />
    </div>
  );
}

function PartsCatalogModal({ parts, canSeeCost = true, onClose, onChange }) {
  const [q, setQ] = useState("");
  const [form, setForm] = useState({ name: "", color: "", value: "", cost: "" });
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);

  const filtered = useMemo(() => {
    const s = q.toLowerCase().trim();
    if (!s) return parts;
    return parts.filter((p) => p.name.toLowerCase().includes(s) || (p.color || "").toLowerCase().includes(s));
  }, [parts, q]);

  const save = async () => {
    if (!form.name.trim() || saving) return;
    setSaving(true);
    try {
      const payload = { name: form.name.trim(), color: form.color || "", value: parseCurrency(form.value) };
      if (canSeeCost) payload.cost = parseCurrency(form.cost);
      if (editingId) await api.patch(`/parts/${editingId}`, payload);
      else await api.post("/parts", payload);
      setForm({ name: "", color: "", value: "", cost: "" }); setEditingId(null); onChange();
    } finally { setSaving(false); }
  };

  const edit = (p) => { setEditingId(p.id); setForm({ name: p.name, color: p.color || "", value: p.value, cost: p.cost }); };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] shadow-2xl overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-blue-600 to-blue-800 text-white flex items-center justify-between">
          <div>
            <h3 className="font-heading text-lg font-bold">Cadastro de peças</h3>
            <p className="text-xs text-white/70 mt-0.5">Peças pré-cadastradas para agilizar as vendas</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto">
          {/* Form */}
          <div className="bg-blue-50/40 rounded-lg border border-blue-200 p-3">
            <div className="text-[11px] uppercase font-bold text-blue-800 tracking-wider mb-2">
              {editingId ? "Editar peça" : "Nova peça"}
            </div>
            <div className={`grid grid-cols-1 gap-2 ${canSeeCost ? "md:grid-cols-5" : "md:grid-cols-4"}`}>
              <div className="md:col-span-2">
                <label className="text-[10px] uppercase font-semibold text-slate-500">Nome</label>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex.: Tela iPhone 14 Pro" className="w-full h-9 rounded-md border border-slate-300 px-2.5 text-sm focus:border-blue-500 focus:outline-none" data-testid="part-name" />
              </div>
              <div>
                <label className="text-[10px] uppercase font-semibold text-slate-500">Cor</label>
                <select value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="w-full h-9 rounded-md border border-slate-300 px-2 text-sm bg-white focus:border-blue-500 focus:outline-none" data-testid="part-color">
                  <option value="">—</option>
                  {COLORS.map((c) => (<option key={c}>{c}</option>))}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase font-semibold text-slate-500">Valor</label>
                <input type="number" step="0.01" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className="w-full h-9 rounded-md border border-slate-300 px-2.5 text-sm text-right focus:border-blue-500 focus:outline-none" data-testid="part-value" />
              </div>
              {canSeeCost && (
                <div>
                  <label className="text-[10px] uppercase font-semibold text-slate-500">Custo</label>
                  <input type="number" step="0.01" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} className="w-full h-9 rounded-md border border-slate-300 px-2.5 text-sm text-right focus:border-blue-500 focus:outline-none" data-testid="part-cost" />
                </div>
              )}
            </div>
            <div className="mt-2 flex justify-end gap-2">
              {editingId && (
                <button onClick={() => { setEditingId(null); setForm({ name: "", color: "", value: "", cost: "" }); }} className="h-8 px-3 rounded-md text-xs font-medium text-slate-600 hover:bg-slate-200">Cancelar</button>
              )}
              <button onClick={save} disabled={saving || !form.name.trim()} data-testid="part-save" className="h-8 px-3 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-50">
                <Save className="w-3.5 h-3.5" /> {editingId ? "Salvar alterações" : "Adicionar peça"}
              </button>
            </div>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar peça..." className="w-full h-10 pl-9 pr-3 rounded-lg border border-slate-300 text-sm focus:border-blue-500 focus:outline-none" />
          </div>

          {/* List */}
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            {filtered.length === 0 ? (
              <div className="p-8 text-center text-sm text-slate-400 italic">
                {parts.length === 0 ? "Nenhuma peça cadastrada ainda." : "Nenhum resultado."}
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left text-[10px] font-bold uppercase text-slate-500 px-3 py-2">Nome</th>
                    <th className="text-left text-[10px] font-bold uppercase text-slate-500 px-3 py-2">Cor</th>
                    <th className="text-right text-[10px] font-bold uppercase text-slate-500 px-3 py-2">Valor</th>
                    {canSeeCost && (
                      <th className="text-right text-[10px] font-bold uppercase text-slate-500 px-3 py-2">Custo</th>
                    )}
                    <th className="w-20"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((p) => {
                    const swatch = COLOR_MAP[p.color];
                    return (
                      <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50/50">
                        <td className="px-3 py-2 text-sm font-medium text-slate-800">{p.name}</td>
                        <td className="px-3 py-2 text-xs text-slate-600">
                          {p.color ? (
                            <span className="inline-flex items-center gap-1.5">
                              {swatch && <span className="w-3 h-3 rounded-full border border-slate-300" style={{ background: swatch }} />}
                              {p.color}
                            </span>
                          ) : "—"}
                        </td>
                        <td className="px-3 py-2 text-right font-semibold text-blue-700 tabular-nums whitespace-nowrap">R$ {fmtBRL(p.value)}</td>
                        {canSeeCost && (
                          <td className="px-3 py-2 text-right font-semibold text-rose-700 tabular-nums whitespace-nowrap">R$ {fmtBRL(p.cost)}</td>
                        )}
                        <td className="px-3 py-2 text-right">
                          <button onClick={() => edit(p)} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded" data-testid={`part-edit-${p.id}`}><Edit3 className="w-3.5 h-3.5" /></button>
                          <button onClick={() => setConfirmDel(p)} className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded" data-testid={`part-delete-${p.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <ConfirmDialog
          open={!!confirmDel}
          title="Excluir peça do cadastro"
          message={confirmDel ? `Remover "${confirmDel.name}" do cadastro?` : ""}
          confirmLabel="Excluir"
          onCancel={() => setConfirmDel(null)}
          onConfirm={async () => { const id = confirmDel.id; setConfirmDel(null); await api.delete(`/parts/${id}`); onChange(); }}
        />
      </div>
    </div>
  );
}
